<template>
    <div>
        <Component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <Component :is="story.content.component" v-if="story" :blok="story.content" :products="products" />
        </main>
        <Component
            :is="globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IAirportProducts, IParkingSummaryInfo } from '@nuxt-monorepo/ui/types';
import { getCouponsSummary, getProductsFromResponse } from '~/services/apiProductService';
import type { ComputedRef } from 'vue';
const globalStore: any = useGlobalStore();
const { locale, t } = useI18n();
const route: any = useRoute();

// Constant variable for access to Runtime Config
const { STORYBLOK_FOLDER, STORYBLOK_VERSION, STORYBLOK_ROUTE_BLOCKED }: any = useRuntimeConfig().public;

// Need to resolve shared components to allow the storyblok API
// to expose this for us to use.
const resolveRelations: string[] = [
    'APRCouponPage.globals',
    'Page.globals',
    'CoreSharedComponent.SharedComponent',
    'PartnersContainer.Partners',
];

const sbLocation = `/${STORYBLOK_FOLDER}/pages/v2/airports/parking-coupons/${route.params.IATA}`;

const isInVisualEditor = !!route?.query?.['_storyblok_tk[token]']; //Check if frontend is opened in visual editor
// Perform request to Storyblok to retrieve content
const data: any = await useAsyncStoryblok(sbLocation, {
    // Enable deep reactivity to fix live editing when in visual editor.
    // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
    deep: isInVisualEditor,
    api: {
        version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
        resolve_relations: resolveRelations,
    },
    bridge: {
        resolveRelations: resolveRelations,
    },
});

const story = data?.story;
const error = data?.error;

if (story) {
    let config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string } = {
        STORYBLOK_VERSION: STORYBLOK_VERSION,
        STORYBLOK_FOLDER: STORYBLOK_FOLDER,
    };
    setSeoMeta(story.value?.content?.seo, config, route);
    if (story.value?.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}

function getStoryblokBlockedRoutes() {
    if (STORYBLOK_ROUTE_BLOCKED) {
        const blocked = STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.path.includes(block));
    } else {
        return false;
    }
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes()) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (error?.value) {
    throw createError({
        statusCode: error.value.statusCode ?? 500,
        statusMessage: error.value.statusMessage ?? 'Failed to load page',
        fatal: true,
    });
}

onMounted(() => {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), {
        resolveRelations,
    });
});

const apiEntity: any = (story.value.content.api_entity || [])[0];
const slug = apiEntity?.og_slug;

const apr: any = await useAsyncData(
    async () =>
        await $privateApi('contents/pages', {
            method: 'POST',
            body: {
                domain: 'apr.com',
                slug,
            },
        }),
    { lazy: true }
);

const products: IAirportProducts = getProductsFromResponse(apr);
const parkingInfo = getCouponsSummary(apr);
const summaryInfo: ComputedRef<IParkingSummaryInfo> = computed(() => {
    return {
        title: `${parkingInfo.code} Parking Coupon & Promo Code Summary`,
        data: [
            {
                label: `${t('latestCoupon')}:`,
                value: parkingInfo.coupon,
            },
            {
                label: `${t('averageSavings')}:`,
                value: '$5.00',
            },
            {
                label: `${t('redeemable')}:`,
                value: t('onlineAtCheckout'),
            },
            {
                label: `${t('validAt')} ${parkingInfo.code}:`,
                value: `${parkingInfo.valid} ${t('parkingLots')}`,
            },
        ],
    };
});
provide('parkingSummary', summaryInfo);
</script>
